/**
 * useDisplayLevel — THE canonical user-facing level for the PWA.
 *
 * iOS source: client-native/src/hooks/useCurrentLevel.ts +
 * `computeCurrentLevel` in client-native/src/stores/globalStore.ts.
 * iOS NEVER displays the server's `GetUserStatsResponse.level` (a pure
 * listening-hours curve that hits level 6 after ~5 minutes); it computes:
 *
 *   liveLevel = activationStep >= ACTIVATION_COMPLETE_STEP
 *     ? getPostActivationLevelForListeningTime(listeningSeconds)   // levels 10-50
 *     : getActivationLevel(stats, { repairMissingReviewedCards: true }) // levels 1-9
 *   level = max(userSettings.highestEarnedLevel, liveLevel)
 *
 * The server-synced `highestEarnedLevel` is a monotonic floor, so live stat
 * changes can raise the level but never lower it.
 *
 * How call sites should use it:
 *
 *   const { level, isReady } = useDisplayLevel();
 *
 *   - Display (Learn header, profile CurrentLevelCard, LevelsSheet): render
 *     `level` directly. It reads 1 while the backing queries load, then settles
 *     on the real value — never read `userStats.level` for display.
 *   - Level-up celebrations: don't call this directly — `useLevelUp()` already
 *     consumes it internally; just call `useLevelUp()` with no argument.
 *   - Anything that must not react to the loading default (queueing
 *     celebrations, analytics, persistence) must gate on `isReady`.
 */
import {
  ACTIVATION_COMPLETE_STEP,
  getActivationLevel,
  getPostActivationLevelForListeningTime,
  useActivationJourney,
} from "@/features/activation";
import { useUserSettings } from "@/hooks/useUserSettings";

export interface UseDisplayLevelResult {
  /** The activation-aware display level (1..50). 1 while loading. */
  level: number;
  /** True once user settings AND the backing stats have loaded. */
  isReady: boolean;
}

export function useDisplayLevel(): UseDisplayLevelResult {
  const { settings } = useUserSettings();
  const journey = useActivationJourney();

  const activationStep = settings?.activationStep ?? 1;
  const liveLevel =
    activationStep >= ACTIVATION_COMPLETE_STEP
      ? getPostActivationLevelForListeningTime(
          journey.stats.listeningTimeSeconds,
        )
      : getActivationLevel(journey.stats, {
          repairMissingReviewedCards: true,
        });

  return {
    level: Math.max(settings?.highestEarnedLevel ?? 1, liveLevel),
    isReady: journey.isReady,
  };
}
